import { initFirebase } from '../firebase'
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore'

const { db } = initFirebase()

// Horoscope podcast generations (getHoroscopePodcast). One doc per generation.
// Read directly from Firestore via the client SDK — the deployed `ryomen` rules are
// `allow read, write: if request.auth != null` and the app signs in anonymously on
// startup, same as transcriptions / aiUsage.
//
// A whole date range is loaded at once (single-field index on `date`, so no composite
// index needed): where date in [start,end] orderBy date desc. Charts group by `date`.

const COLLECTION = 'reading_generations'

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

function bool(v) {
  return v === true
}

function toMs(v) {
  if (!v) return 0
  if (typeof v === 'object' && typeof v.toMillis === 'function') return v.toMillis()
  if (typeof v === 'object' && (v.seconds || v._seconds)) return (v.seconds || v._seconds) * 1000
  const t = new Date(v).getTime()
  return isNaN(t) ? 0 : t
}

export class FirebaseReadingGenerationsService {
  // Load every generation whose horoscope `date` falls in [startDate, endDate].
  // Returns { items, truncated } — truncated is true if the safety cap was hit.
  async getByDateRange({ startDate, endDate, max = 5000 }) {
    const col = collection(db, COLLECTION)
    const q = query(
      col,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc'),
      limit(max)
    )
    const snap = await getDocs(q)
    return {
      items: snap.docs.map(d => this._toEntry(d)),
      truncated: snap.size === max
    }
  }

  // The playable audio + script live in `readings/{readingId}` (audioUrl is a
  // Storage download URL), not on the generation-log doc. Fetched lazily when an
  // admin expands a row, so we don't do one read per row up front.
  async getReadingDetail(readingId) {
    if (!readingId) return null
    const snap = await getDoc(doc(db, 'readings', readingId))
    if (!snap.exists()) return null
    const x = snap.data()
    return {
      audioUrl: x.audioUrl || '',
      title: x.title || '',
      subtitle: x.subtitle || '',
      summary: x.summary || '',
      durationSec: num(x.audioDurationSec),
      dialogueTurns: Array.isArray(x.dialogueTurns) ? x.dialogueTurns : [],
      suggestedQuestions: Array.isArray(x.suggestedQuestions) ? x.suggestedQuestions : []
    }
  }

  _toEntry(docSnap) {
    const d = docSnap.data()
    return {
      id: docSnap.id,
      userId: d.userId || '',
      readingId: d.readingId || '',
      date: d.date || '',
      type: d.type || '',
      language: d.language || '',
      usedTransits: bool(d.usedTransits),
      usedChatTheme: bool(d.usedChatTheme),
      transitMs: num(d.transitMs),
      textGenMs: num(d.textGenMs),
      audioSynthMs: num(d.audioSynthMs),
      totalMs: num(d.totalMs),
      audioEngine: d.audioEngine || '',
      audioChars: num(d.audioChars),
      audioBytes: num(d.audioBytes),
      audioDurationSec: num(d.audioDurationSec),
      ttsRatePer1kChars: num(d.ttsRatePer1kChars),
      ttsCostUsd: num(d.ttsCostUsd),
      textGenerated: bool(d.textGenerated),
      audioGenerated: bool(d.audioGenerated),
      timestampMs: toMs(d.createdAt) || toMs(d.timestamp)
    }
  }
}
